'use es6';

import { List, Map as ImmutableMap } from 'immutable';
import { getProperty, getPropertyLabel, getReferenceLabel } from 'reporting-data/v2/dataset/datasetRecords';
import { getPropertyFormatter, formatPercent, formatDate, DateFormats } from 'reporting-visualizations/formatters/propertyFormatter';
import { hasReferences } from 'reporting-data/v2/dataset/datasetRecords';
import { ACCESSIBILITY_DATE_FORMATS } from 'reporting-data/constants/dateFormats';
import { getPatternFormat } from '../../constants/colors';
import { DEFAULT_DEBUG } from '../../constants/debug';
import { selectAndSortFieldValues } from '../../field/sort';
import { isFormattableProperty } from '../../formatters/propertyFormatter';
import { applyOrder, getDataKeys, groupData } from '../../highcharts/chartUtils';
import { SlicedChart, SlicedChartSeries, SlicedPoint, SlicedStyle } from './slicedRecords';
const getSliceColor = (sliceKey, defined, definedPattern) => {
  if (definedPattern.isEmpty()) {
    return defined.get(sliceKey);
  } else {
    return getPatternFormat(definedPattern.get(sliceKey), defined.get(sliceKey));
  }
};
const getGrandTotal = ({
  dataSet,
  grandTotalColumn
}) => {
  if (grandTotalColumn) {
    var _dataSet$get;
    const firstRow = dataSet === null || dataSet === void 0 || (_dataSet$get = dataSet.get('data')) === null || _dataSet$get === void 0 ? void 0 : _dataSet$get.first();
    if (firstRow) {
      return firstRow.get(grandTotalColumn);
    }
  }
  return undefined;
};
const createAccessibilityLabel = (name, maybeFormattableColorProperty, dateResolution, dataLabelPointFormatter, sliceValue, total, sliceKey) => {
  const isDateProperty = maybeFormattableColorProperty && (maybeFormattableColorProperty.get('type') === DateFormats.date || maybeFormattableColorProperty.get('type') === DateFormats.datetime || maybeFormattableColorProperty.get('type') === DateFormats.timestamp);
  const sliceName = isDateProperty && dateResolution ? formatDate(sliceKey, {
    format: ACCESSIBILITY_DATE_FORMATS[dateResolution],
    method: 'userTz'
  }) : name;
  const percentage = total > 0 ? sliceValue / total : 0;
  const slicePercent = formatPercent(percentage);
  const formattedSliceValue = dataLabelPointFormatter(sliceValue);
  return `${sliceName}: ${slicePercent}, (${formattedSliceValue})`;
};
const mapPieSeries = (layer, isGrouped, {
  onPointClick
} = {}, dateResolution) => {
  const {
    dataset,
    angle: {
      field: angleField
    },
    color: {
      field: colorField,
      order: colorOrder,
      defined = ImmutableMap(),
      default: colorDefault,
      definedPatterns = ImmutableMap()
    },
    style,
    colorFormattingOptions,
    valueFormattingOptions
  } = layer;
  const {
    data
  } = dataset;
  const sliceOrder = colorOrder ? selectAndSortFieldValues(dataset, colorField, colorOrder) : applyOrder(getDataKeys(dataset, colorField), colorOrder);
  const maybeFormattableColorProperty = getProperty(dataset, colorField);
  const colorProperty = isFormattableProperty(maybeFormattableColorProperty) && colorFormattingOptions ? maybeFormattableColorProperty.set('formattingType', colorFormattingOptions.getIn(['format', 'type'])).set('formattingObject', colorFormattingOptions.get('format')) : undefined;
  const slices = isGrouped ? groupData(data, [colorField]).sortBy((_, key) => sliceOrder.indexOf(key)) : dataset.getIn(['data', 0]).map(() => dataset.get('data'));

  // Calculate total for percentage calculation
  const pointField = isGrouped ? angleField : slices.keySeq().first();
  const valueOptionsForPoint = valueFormattingOptions ? valueFormattingOptions.filter(option => {
    return option.column === pointField;
  }).first().options : undefined;
  const maybeFormattableProperty = getProperty(dataset, pointField);
  const property = isFormattableProperty(maybeFormattableProperty) && valueOptionsForPoint ? maybeFormattableProperty.set('formattingType', valueOptionsForPoint.getIn(['format', 'type'])).set('formattingObject', valueOptionsForPoint.get('format')) : maybeFormattableProperty;
  const dataLabelPointFormatter = getPropertyFormatter(property);

  // Calculate total value for percentage calculation
  const total = slices.reduce((acc, sliceRow, sliceKey) => {
    const sliceField = isGrouped ? angleField : sliceKey;
    return acc + sliceRow.first().get(sliceField);
  }, 0);
  const points = slices.map((sliceRow, sliceKey) => {
    const propertyUsesReferences = hasReferences(dataset, colorField, sliceKey);
    const label = propertyUsesReferences || !colorProperty ? getReferenceLabel(dataset, colorField, sliceKey) : getPropertyFormatter(colorProperty)(sliceKey);
    const name = isGrouped ? label : getPropertyLabel(dataset, label);
    const sliceField = isGrouped ? angleField : label;
    const sliceValue = sliceRow.first().get(sliceField);
    const accessibilityLabel = createAccessibilityLabel(name, maybeFormattableColorProperty, dateResolution, dataLabelPointFormatter, sliceValue, total, sliceKey);
    return SlicedPoint({
      name,
      y: sliceValue,
      color: getSliceColor(String(sliceKey), defined, definedPatterns) || colorDefault,
      sliceRow: sliceRow.first(),
      accessibilityLabel
    });
  }).toList();
  const events = onPointClick ? {
    click(event) {
      const point = points.get(event.point.index);
      onPointClick({
        point,
        layer,
        dataset
      });
    }
  } : {};
  return List([SlicedChartSeries({
    name: getPropertyLabel(dataset, angleField),
    points,
    dataLabelPointFormatter,
    events,
    innerSize: style.get('innerSize', '0%')
  })]);
};
export const mapVizToChart = (chartVisualization, debug = DEFAULT_DEBUG) => {
  const {
    data,
    layer,
    events = ImmutableMap(),
    style = SlicedStyle(),
    isGrouped = true
  } = chartVisualization;
  const series = mapPieSeries(layer.update('dataset', dataset => data.get(dataset)), isGrouped, events, style.get('dateResolution'));
  const {
    grandTotalColumn
  } = layer || {};
  const grandTotal = getGrandTotal({
    dataSet: data.get('primary'),
    grandTotalColumn
  });
  const options = {
    type: 'pie',
    grandTotal,
    dataLabels: style.get('dataLabels') !== undefined ? style.get('dataLabels') : true,
    legendPosition: style.get('legendPosition'),
    legendWidth: style.get('legendWidth'),
    showTotals: style.get('showTotals'),
    tooltipDisabled: style.get('isMobile') || style.get('tooltipDisabled'),
    disableSeriesHighlighting: style.get('isMobile'),
    dateResolution: style.get('dateResolution')
  };
  const chart = SlicedChart({
    series,
    options
  });
  debug.set('Sliced Chart', chart);
  return chart;
};